import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, X, Crown, Award, Sparkles, ArrowRight, Heart, Zap,
  BookOpen, Mail, Users, Layers, Shield, Globe, GraduationCap,
} from "lucide-react";
import Brand from "./Brand";

/* ThreeTierPricing — flagship pricing block. THREE columns:
   Free Trial (7-day), Pro ($49), Unlimited ($79).

   Mike's rule (Feb 2026): "Only 2 paid levels — $49 and $79. The only
   difference between $49 and $79 is contacts and book covers. That's it."
   So everything else checks on both paid tiers. */

const G    = "#2E9F3A";
const O    = "#F39A1F";
const WINE = "#0F172A";
const NAVY = "#0F172A";

/* Tier data — only the CONTACTS and BOOK COVERS limits change between
   the two paid tiers. Differences are surfaced at the top of each card. */
const TIERS = [
  {
    key: "seed",
    name: "Free Trial",
    tagline: "7 Days Free. Then Pick Your Plan.",
    priceMonthly: 0,
    priceAnnual: 0,
    accent: O,
    popular: false,
    cta: "Start Free Trial",
    ctaTo: "/signup?plan=free",
    contacts: "100 contacts",
    bookCovers: "1 book cover",
  },
  {
    key: "growth",
    name: "Pro",
    tagline: "Everything You Need To Get Started.",
    priceMonthly: 49,
    priceAnnual: 490,
    accent: G,
    popular: true,
    cta: "Become A Builder · $49/mo",
    ctaTo: "/signup?plan=faithful_monthly",
    contacts: "10,000 contacts",
    bookCovers: "10 book covers/month",
  },
  {
    key: "unlimited",
    name: "Unlimited",
    tagline: "No Limits. Built To Scale.",
    priceMonthly: 79,
    priceAnnual: 790,
    accent: "#7C2A2A",
    popular: false,
    cta: "Go Unlimited · $79/mo",
    ctaTo: "/signup?plan=kingdom_unlimited_monthly",
    contacts: "UNLIMITED contacts",
    bookCovers: "UNLIMITED book covers",
  },
];

/* Same features for every tier. Only contacts and book covers differ
   between $49 and $79. */
const SHARED_FEATURES = [
  "Your own page at faitharama.com/m/you",
  "Build as many sign-up pages as you want",
  "Send unlimited emails to your list",
  "AI Writer for sales copy, emails, posts",
  "Full Academy — every course included",
  "Mirror Site builder with starter templates",
  "Auto follow-up emails to new sign-ups",
  "Monthly Live Workshop with Mike",
  "Free flipbook lead magnets to give away",
  "Flat 50/50 affiliate split on every sale",
  "Stripe checkout · cancel anytime",
  "30-day money-back guarantee",
];

export default function ThreeTierPricing() {
  const [annual, setAnnual] = useState(false);

  return (
    <section className="relative overflow-hidden py-14 md:py-20"
             style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #EAF6FC 100%)" }}
             data-testid="three-tier-pricing">
      <div className="max-w-7xl mx-auto px-5 md:px-8">

        {/* HEADLINE */}
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] tracking-[0.3em] uppercase font-bold mb-3"
               style={{ background: "#0F172A", color: O }}>
            <Heart className="w-3.5 h-3.5"/> TWO PAID PLANS · ONE MINISTRY
          </div>
          <h2 className="font-serif-display text-3xl sm:text-5xl md:text-6xl uppercase leading-[1.02]"
              style={{ color: WINE, textWrap: "balance" }}>
            Pick Your Plan.<br/>
            <span style={{ color: O }}>Join The Ministry.</span>
          </h2>
          <p className="mt-4 text-base md:text-xl text-foreground/85 leading-relaxed">
            Same tools. Same support. The only difference between $49 and $79 is contacts and book covers.
          </p>

          {/* Annual / Monthly toggle */}
          <div className="mt-6 inline-flex items-center gap-2 p-1.5 rounded-full shadow-sm bg-white"
               style={{ border: `2px solid ${O}55` }}
               data-testid="pricing-billing-toggle">
            <button onClick={() => setAnnual(false)}
                    data-testid="toggle-monthly"
                    className={`px-5 py-2 rounded-full text-xs tracking-[0.18em] uppercase font-bold transition ${!annual ? "text-white shadow" : "text-foreground/65"}`}
                    style={{ background: !annual ? WINE : "transparent" }}>
              Monthly
            </button>
            <button onClick={() => setAnnual(true)}
                    data-testid="toggle-annual"
                    className={`px-5 py-2 rounded-full text-xs tracking-[0.18em] uppercase font-bold transition ${annual ? "text-white shadow" : "text-foreground/65"}`}
                    style={{ background: annual ? WINE : "transparent" }}>
              Annual <span className="text-[10px] ml-1" style={{ color: annual ? "#E0F2FE" : G }}>SAVE 2 MONTHS</span>
            </button>
          </div>
        </div>

        {/* 3 CARDS */}
        <div className="grid lg:grid-cols-3 gap-4 md:gap-5 items-stretch" data-testid="pricing-cards">
          {TIERS.map((t) => {
            const price = annual ? Math.round(t.priceAnnual / 12) : t.priceMonthly;
            const subtitle = annual ? `Billed $${t.priceAnnual}/yr` : (t.priceMonthly === 0 ? "Free forever" : "Billed monthly");
            return (
              <div key={t.key}
                   data-testid={`pricing-card-${t.key}`}
                   className={`relative rounded-2xl bg-white p-6 md:p-7 flex flex-col transition hover:shadow-2xl ${t.popular ? "shadow-2xl -translate-y-1" : "shadow-md"}`}
                   style={{
                     border: t.popular ? `4px solid ${O}` : `2px solid ${t.accent}33`,
                   }}>
                {t.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] tracking-[0.2em] uppercase font-bold text-white shadow"
                        style={{ background: O }}>
                    ★ MOST POPULAR
                  </span>
                )}

                {/* Header */}
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: t.accent }}>
                  ★ {t.name.toUpperCase()}
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight mt-1"
                    style={{ color: WINE, textWrap: "balance" }}>
                  {t.tagline}
                </h3>

                {/* Price */}
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-serif-display text-5xl md:text-6xl" style={{ color: WINE }}>
                    ${price}
                  </span>
                  {t.priceMonthly > 0 && (
                    <span className="text-sm font-bold uppercase tracking-[0.18em] text-foreground/55">
                      / month
                    </span>
                  )}
                </div>
                <div className="text-xs text-foreground/65 mt-1">
                  {subtitle}{t.priceMonthly > 0 && " · 7-day free trial"}
                </div>

                {/* CTA */}
                <Link to={t.ctaTo}
                      data-testid={`pricing-cta-${t.key}`}
                      className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.02] transition"
                      style={{ background: t.accent }}>
                  {t.cta} <ArrowRight className="w-4 h-4"/>
                </Link>

                {/* WHAT'S DIFFERENT ON THIS TIER — only contacts + books change */}
                <div className="mt-6 p-4 rounded-lg" style={{ background: "#E0F2FE", border: `2px solid ${t.accent}` }}
                     data-testid={`pricing-diffs-${t.key}`}>
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: t.accent }}>
                    ★ This Tier's Limits
                  </div>
                  <div className="flex items-start gap-2 text-sm font-bold mb-1.5" style={{ color: WINE }}>
                    <Users className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: t.accent }}/>
                    <span>{t.contacts}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm font-bold" style={{ color: WINE }}>
                    <BookOpen className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: t.accent }}/>
                    <span>{t.bookCovers}</span>
                  </div>
                </div>

                {/* SHARED — Everything Else Included (same on all 4 tiers) */}
                <div className="mt-5">
                  <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2.5" style={{ color: G }}>
                    ★ Everything Else Included
                  </div>
                  <ul className="space-y-1.5 text-sm" data-testid={`pricing-features-${t.key}`}>
                    {SHARED_FEATURES.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 leading-snug">
                        <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: G }}/>
                        <span className="text-foreground/90">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Courses preview — only on Pro card */}
                {t.key === "pro" && (
                  <div className="mt-6 pt-5 border-t" style={{ borderColor: `${WINE}22` }}
                       data-testid="kingdom-academy-preview">
                    <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-3" style={{ color: WINE }}>
                      ★ FULL ACADEMY INCLUDED
                    </div>
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      Every course in <Link to="/dashboard/academy" className="font-bold underline" style={{ color: O }}>The Academy</Link> — platform tutorials plus the Affiliate Courses playbook. New lessons drop as Mike records them.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* TRUST / FAQ MICRO ROW */}
        <div className="mt-10 grid sm:grid-cols-3 gap-4" data-testid="pricing-trust-row">
          {[
            { Icon: Shield,    title: "Cancel Anytime",       sub: "No contracts. No surprise charges. Stripe-secured." },
            { Icon: Sparkles,  title: "Real Founder Support", sub: "Mike Boyd reads every Pro 'Help Me, Mike!' message." },
            { Icon: Award,     title: "30-Day Refund",         sub: "Not happy? Email us within 30 days, get a full refund." },
          ].map((row) => (
            <div key={row.title} className="rounded-xl px-4 py-4 flex items-start gap-3"
                 style={{ background: "#FFFFFF", border: `1.5px solid ${O}33` }}>
              <row.Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: WINE }}/>
              <div>
                <div className="text-sm font-bold" style={{ color: WINE }}>{row.title}</div>
                <div className="text-xs text-foreground/70 mt-0.5">{row.sub}</div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
